import React, { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import { FieldProps } from "react-jsonschema-form";
import { useChainData } from "../../components/chain-data-provider";
import { AccommodationSortingCriteria } from "./domain/behaviour.entity";
import { isValidTickerList } from "./domain/customEstablishmentOrder";
import { messages } from "./messages";

const Container = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const HelpText = styled.small`
  color: #6c757d;
  display: block;
  margin-top: 0.25rem;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const AvailableTickers = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const Label = styled(Form.Label)`
  font-weight: 500;
`;

interface FormContext {
  rootFormData?: {
    accommodationsOrder?: string;
    customEstablishmentOrder?: string;
  };
}

export function CustomEstablishmentOrderWidget({ formData, onChange, formContext }: FieldProps) {
  const intl = useIntl();
  const chainData = useChainData();

  const rootFormData = (formContext as FormContext)?.rootFormData;
  const value = (formData as string | undefined) ?? "";

  const shouldShow =
    chainData?.isChain &&
    chainData.children.length > 0 &&
    rootFormData?.accommodationsOrder === AccommodationSortingCriteria.NO_SORTING;

  const error = useMemo(() => {
    if (!isValidTickerList(value)) {
      return intl.formatMessage(messages.invalidTickerFormat);
    }
    return null;
  }, [value, intl]);

  // Set default value when widget becomes visible and has no value
  useEffect(() => {
    if (shouldShow && !formData && chainData?.children.length) {
      onChange(chainData.children.join(","));
    }
  }, [shouldShow, formData, chainData?.children, onChange]);

  // Clear value when widget becomes hidden
  useEffect(() => {
    if (!shouldShow && formData) {
      onChange(undefined);
    }
  }, [shouldShow, formData, onChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value || undefined);
    },
    [onChange]
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <Container>
      <Form.Group>
        <Label>
          <FormattedMessage {...messages.customEstablishmentOrder} />
        </Label>
        <Form.Control
          as="textarea"
          rows={5}
          value={value}
          onChange={handleChange}
          isInvalid={!!error}
          placeholder="ticker1,ticker2,ticker3"
        />
        {error && <ErrorText>{error}</ErrorText>}
        <HelpText>
          <FormattedMessage {...messages.customEstablishmentOrderHelp} />
        </HelpText>
        <AvailableTickers>
          <strong>
            <FormattedMessage {...messages.availableMotors} />:{" "}
          </strong>
          {chainData?.children.join(", ")}
        </AvailableTickers>
      </Form.Group>
    </Container>
  );
}
